import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { AUTHZ_SCOPES, ROLES } from "@nebulize/sdk";
import type { AuthzScope, Role, RoleAssignment, ScopeRef } from "@nebulize/sdk";

import {
  Button,
  DataTable,
  EmptyState,
  Field,
  Input,
  Page,
  Panel,
  Select,
  Stack,
  StatusBadge,
} from "@/components/ui";

import { Paged } from "../components/Paged";

import { QueryPanel } from "./components/QueryPanel";
import { useAccount, useGrantRole, useRevokeRole, useRoles } from "./lib/queries";
import { errorText, roleLabel, scopeLabel, stamp } from "./lib/present";

function matchAssignment(row: RoleAssignment, needle: string): boolean {
  return `${row.role} ${row.scope.scope} ${row.scope.key ?? ""}`.toLowerCase().includes(needle);
}

function scopeRefOf(scope: AuthzScope, key: string): ScopeRef {
  return key.trim() === "" ? { scope } : { scope, key: key.trim() };
}

/** Who holds which role, in which scope. Two platform roles exist and no screen invents a third. */
export function ConsolePeople() {
  const navigate = useNavigate();
  const account = useAccount();
  const search = useRouterState({ select: (state) => state.location.search }) as Record<
    string,
    unknown
  >;
  const fromUrl = typeof search["subject"] === "string" ? search["subject"] : "";
  const subjectId = fromUrl === "" ? (account.data?.account_id ?? "") : fromUrl;

  const roles = useRoles(subjectId);
  const grant = useGrantRole();
  const revoke = useRevokeRole(subjectId);

  const [typed, setTyped] = useState("");
  const [role, setRole] = useState<Role>("account_owner");
  const [scope, setScope] = useState<AuthzScope>("account");
  const [key, setKey] = useState("");

  return (
    <Page title="People">
      <Panel title="Actor">
        <form
          className="cx-listbar"
          onSubmit={(event) => {
            event.preventDefault();
            void navigate({ to: "/console/people", search: { subject: typed.trim() } });
          }}
        >
          <Field label="Account id">
            {({ id }) => (
              <Input
                id={id}
                mono
                value={typed}
                placeholder={subjectId}
                onChange={(event) => {
                  setTyped(event.target.value);
                }}
              />
            )}
          </Field>
          <Button type="submit" size="sm" variant="secondary">
            Read roles
          </Button>
          {fromUrl === "" ? null : (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setTyped("");
                void navigate({ to: "/console/people", search: {} });
              }}
            >
              Clear
            </Button>
          )}
        </form>
      </Panel>

      <QueryPanel title="Roles" subtitle={subjectId} rows={3} query={roles}>
        {(rows) =>
          rows.length === 0 ? (
            <EmptyState bare headline="No roles" />
          ) : (
            <Paged rows={rows} pageSize={10} placeholder="Find a role" match={matchAssignment}>
              {(page) => (
                <DataTable
                  caption="Roles held by this actor"
                  density="comfortable"
                  columns={[
                    {
                      key: "role",
                      header: "Role",
                      emphasis: true,
                      render: (row: RoleAssignment) => roleLabel(row.role),
                    },
                    {
                      key: "scope",
                      header: "Scope",
                      render: (row: RoleAssignment) => (
                        <StatusBadge tone="info">
                          {scopeLabel(row.scope.scope, row.scope.key)}
                        </StatusBadge>
                      ),
                    },
                    {
                      key: "granted_at",
                      header: "Granted",
                      muted: true,
                      render: (row: RoleAssignment) => stamp(row.granted_at),
                    },
                    {
                      key: "granted_by",
                      header: "By",
                      mono: true,
                      render: (row: RoleAssignment) => row.granted_by ?? "platform",
                    },
                    {
                      key: "revoke",
                      header: "",
                      align: "right",
                      width: "120px",
                      render: (row: RoleAssignment) => (
                        <Button
                          size="sm"
                          variant="destructive"
                          aria-label={`Revoke ${roleLabel(row.role)}`}
                          disabled={revoke.isPending}
                          onClick={() => {
                            revoke.mutate(row.id);
                          }}
                        >
                          Revoke
                        </Button>
                      ),
                    },
                  ]}
                  rows={page}
                  emptyHeadline="No matches"
                />
              )}
            </Paged>
          )
        }
      </QueryPanel>

      <Panel title="Grant">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            grant.mutate({ subject_id: subjectId, role, scope: scopeRefOf(scope, key) });
          }}
        >
          <Stack gap={4}>
            <div className="cx-listbar">
              <Field label="Role">
                {({ id }) => (
                  <Select
                    id={id}
                    value={role}
                    options={ROLES.map((one) => ({ value: one, label: roleLabel(one) }))}
                    onChange={(event) => {
                      setRole(event.target.value as Role);
                    }}
                  />
                )}
              </Field>
              <Field label="Scope">
                {({ id }) => (
                  <Select
                    id={id}
                    value={scope}
                    options={AUTHZ_SCOPES.map((one) => ({ value: one, label: one }))}
                    onChange={(event) => {
                      setScope(event.target.value as AuthzScope);
                    }}
                  />
                )}
              </Field>
              <Field label="Key">
                {({ id }) => (
                  <Input
                    id={id}
                    mono
                    value={key}
                    disabled={scope === "platform"}
                    onChange={(event) => {
                      setKey(event.target.value);
                    }}
                  />
                )}
              </Field>
              <Button
                type="submit"
                size="sm"
                variant="primary"
                loading={grant.isPending}
                disabled={subjectId === ""}
              >
                Grant
              </Button>
            </div>
            <div aria-live="polite">
              {grant.isError ? (
                <StatusBadge tone="danger">{errorText(grant.error)}</StatusBadge>
              ) : null}
              {revoke.isError ? (
                <StatusBadge tone="danger">{errorText(revoke.error)}</StatusBadge>
              ) : null}
            </div>
          </Stack>
        </form>
      </Panel>
    </Page>
  );
}
